import {Graphics, Rectangle} from "pixi.js";
import {inches} from "../../const";
import {glyphStyle, hasCategory, HardwareGlyph, strikeEdgeX} from "./HardwareGlyph";

// a surface closer seen face-on with the door closed: the body centred on the hub with its
// pinion toward the hinge end, the main arm standing off the pinion (seen end-on, so just a
// short stub), and the forearm lying flat along the frame head from the stub to the shoe
// toward the strike side. On the door the arm sits above the body; on the frame head it
// hangs below. Sizes are typical catalogue values (scene units = mm).
const BODY_LENGTH = 11.375 * inches;
const BODY_HEIGHT = 2.75 * inches;
const BODY_RADIUS = 0.375 * inches;
const PINION_FROM_CENTRE = 0.3;             // share of the body length, toward the hinge end
const PINION_RADIUS = 0.5 * inches;
const STUB_WIDTH = 1.25 * inches;           // the main arm seen end-on
const ARM_RISE = 1.75 * inches;             // forearm centre line off the body face
const ARM_THICKNESS = 0.75 * inches;
const ARM_LENGTH = 9 * inches;              // pinion to the shoe, toward the strike side
const SHOE_LENGTH = 2.5 * inches;
const SHOE_HEIGHT = 1 * inches;
const OUTLINE = 0.25 * inches;
const HIT_PAD = 1 * inches;

export const surfaceCloser: HardwareGlyph = {
    id: "surfaceCloser",
    targets: ["door", "frame"],
    matches: item => hasCategory(item, "Surface Closer", "Surface Closer with Hold Open Arm", "Surface Closer with Stop Arm"),
    draw(g: Graphics, ctx): Rectangle {
        const {color, alpha} = glyphStyle(ctx.selected, ctx.invalid);
        const cx = ctx.hub.x;
        const cy = ctx.hub.y;
        // `dir` points from the strike edge toward the hinge edge; the arm reaches the other way.
        const dir = strikeEdgeX(ctx).dir;
        // the arm sits above a door-mounted body and below a frame-mounted one.
        const up = ctx.target === "door" ? -1 : 1;

        const bodyX = cx - BODY_LENGTH / 2;
        const bodyY = cy - BODY_HEIGHT / 2;
        const armFace = cy + up * BODY_HEIGHT / 2;
        const armY = armFace + up * ARM_RISE;
        const pinionX = cx + dir * BODY_LENGTH * PINION_FROM_CENTRE;
        const shoeX = pinionX - dir * ARM_LENGTH;

        // main arm stub: from the body face up to the forearm, over the pinion.
        g.lineStyle(0);
        g.beginFill(color, alpha);
        g.drawRect(pinionX - STUB_WIDTH / 2, Math.min(armFace, armY), STUB_WIDTH, Math.abs(armY - armFace));
        g.endFill();

        // forearm: flat along the head from the stub to the shoe.
        g.lineStyle(OUTLINE, color, alpha);
        g.beginFill(color, alpha * 0.35);
        g.drawRect(Math.min(pinionX, shoeX), armY - ARM_THICKNESS / 2, ARM_LENGTH, ARM_THICKNESS);
        g.endFill();

        g.lineStyle(0);
        g.beginFill(color, alpha);
        g.drawRect(shoeX - dir * SHOE_LENGTH / 2 - SHOE_LENGTH / 2, armY - SHOE_HEIGHT / 2, SHOE_LENGTH, SHOE_HEIGHT);
        g.endFill();

        g.lineStyle(OUTLINE, color, alpha);
        g.beginFill(color, alpha * 0.2);
        g.drawRoundedRect(bodyX, bodyY, BODY_LENGTH, BODY_HEIGHT, BODY_RADIUS);
        g.endFill();

        // pinion on the body face.
        g.beginFill(color, alpha * 0.35);
        g.drawCircle(pinionX, cy, PINION_RADIUS);
        g.endFill();

        const x0 = Math.min(bodyX, shoeX - SHOE_LENGTH);
        const x1 = Math.max(bodyX + BODY_LENGTH, shoeX + SHOE_LENGTH);
        const y0 = Math.min(bodyY, armY - SHOE_HEIGHT / 2);
        const y1 = Math.max(bodyY + BODY_HEIGHT, armY + SHOE_HEIGHT / 2);
        return new Rectangle(x0 - HIT_PAD, y0 - HIT_PAD, x1 - x0 + 2 * HIT_PAD, y1 - y0 + 2 * HIT_PAD);
    },
};
